import { CheckCircleOutline, ErrorOutline, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Collapse, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { blue, green, grey, orange, purple, red } from "../../colors";
import { ThinkingStep } from "../Deepchat/models";

interface ThinkingStepsProps {
  steps: ThinkingStep[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  // When true, steps are revealed one at a time with a minimum gap so a burst of steps that
  // arrive close together doesn't cause the list to jump. Only used for the live response.
  isActive?: boolean;
}

const MIN_STEP_REVEAL_MS = 500;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const animationKeyframes = {
  "@keyframes fadeSlideIn": {
    "0%": { opacity: 0, transform: "translateY(-4px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes ellipsis": {
    "0%": { content: '""' },
    "25%": { content: '"."' },
    "50%": { content: '".."' },
    "75%": { content: '"..."' },
    "100%": { content: '""' },
  },
};

const intentChipColors: Record<string, { bg: string; text: string }> = {
  factual: { bg: blue[100], text: blue[800] },
  comparison: { bg: purple[100], text: purple[800] },
  trend: { bg: "#E0F2F1", text: "#00695C" },
  sentiment: { bg: orange[100], text: orange[800] },
  aggregation: { bg: green[100], text: green[800] },
};
const defaultChipColor = { bg: grey[100], text: grey[700] };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStepDisplayText(step: ThinkingStep): string {
  if (step.step === "intent_classified") {
    return step.status === "active" ? "Identifying intent" : "Identified intent";
  }

  if (step.step === "documents_retrieved" && step.metadata?.document_count != null) {
    const count = step.metadata.document_count as number;
    return `${count} document${count !== 1 ? "s" : ""} found`;
  }

  return step.label;
}

function buildSummaryText(steps: ThinkingStep[]): string {
  const parts: string[] = [];

  for (const step of steps) {
    let text: string;

    if (step.step === "documents_retrieved" && step.metadata?.document_count != null) {
      text = `Analyzed ${step.metadata.document_count} documents`;
    } else if (step.step === "intent_classified") {
      text = step.status === "active" ? "Identifying intent" : "Identified intent";
    } else {
      text = step.label;
    }

    if (parts.at(-1) !== text) {
      parts.push(text);
    }
  }

  return parts.length > 0 ? parts.join(" → ") : "Processed query";
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AutoAwesomeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={grey[400]} style={{ flexShrink: 0 }}>
      <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" />
    </svg>
  );
}

function StepIcon({ status }: { status: ThinkingStep["status"] }) {
  switch (status) {
    case "completed":
      return <CheckCircleOutline sx={{ fontSize: 16, color: green[600] }} />;
    case "failed":
      return <ErrorOutline sx={{ fontSize: 16, color: red[600] }} />;
    case "active":
      return <CircularProgress size={14} thickness={5} sx={{ color: grey[500] }} />;
  }
}

function StepDetail({ step }: { step: ThinkingStep }) {
  const hasDetail = step.detail != null && step.detail !== "";
  if (step.step === "intent_classified") {
    const intentType = step.metadata?.intent_type as string | undefined;
    const chipColor = intentType ? (intentChipColors[intentType] ?? defaultChipColor) : null;
    const chipLabel = intentType ? intentType.charAt(0).toUpperCase() + intentType.slice(1).toLowerCase() : null;
    return (
      <Box mt="2px">
        {chipColor && chipLabel && (
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              fontWeight: 600,
              backgroundColor: chipColor.bg,
              color: chipColor.text,
              mr: "0.5em",
            }}
          />
        )}
        {hasDetail && (
          <Typography variant="caption" color={grey[500]} display="block" mt="2px" fontSize="0.7rem">
            {step.detail}
          </Typography>
        )}
      </Box>
    );
  }
  if (step.step === "clarification_needed") {
    // Single-question mode surfaces the significance-ranking LLM call's duration so the
    // added latency is visible. `ranking_ms` is only present when that mode is enabled.
    const rankingMs = step.metadata?.ranking_ms as number | undefined;
    return (
      <Box mt="2px">
        {rankingMs != null && (
          <Chip
            label={`+${rankingMs} ms ranking`}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              fontWeight: 600,
              backgroundColor: orange[100],
              color: orange[800],
              mr: "0.5em",
            }}
          />
        )}
        {hasDetail && (
          <Typography variant="caption" color={grey[500]} display="block" mt="2px" fontSize="0.7rem">
            {step.detail}
          </Typography>
        )}
      </Box>
    );
  }
  return hasDetail ? (
    <Typography variant="caption" color={grey[500]} mt="2px" fontSize="0.7rem">
      {step.detail}
    </Typography>
  ) : null;
}

function AnimatedStatusLabel({ text, showEllipsis }: { text: string; showEllipsis: boolean }) {
  const [displayText, setDisplayText] = useState(text);
  const [fading, setFading] = useState(false);
  const prevTextRef = useRef(text);

  useEffect(() => {
    if (text !== prevTextRef.current) {
      setFading(true);
      const timeout = setTimeout(() => {
        setDisplayText(text);
        setFading(false);
        prevTextRef.current = text;
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [text]);

  return (
    <Typography
      variant="caption"
      sx={{
        color: grey[700],
        fontWeight: 500,
        flex: 1,
        transition: "opacity 0.2s ease-in-out",
        opacity: fading ? 0 : 1,
        ...(showEllipsis && { "&::after": { content: '""', animation: "ellipsis 1.5s steps(4, end) infinite" } }),
      }}
    >
      {displayText}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Reveals steps one at a time with a minimum gap so a burst of steps arriving close together
// doesn't make the list jump. Returns how many of `total` steps should currently be shown.
// When not paced (historical items), all steps are shown immediately.
function usePacedReveal(total: number, paced: boolean): number {
  const [visible, setVisible] = useState(paced ? Math.min(total, 1) : total);

  useEffect(() => {
    if (!paced) {
      setVisible(total);
      return;
    }
    if (visible >= total) return;
    const timeout = setTimeout(() => setVisible((v) => Math.min(v + 1, total)), MIN_STEP_REVEAL_MS);
    return () => clearTimeout(timeout);
  }, [visible, total, paced]);

  // Never show more than exist (steps can reset between turns).
  return Math.min(visible, total);
}

export default function ThinkingSteps({ steps, collapsed, onToggleCollapse, isActive = false }: ThinkingStepsProps) {
  const visibleCount = usePacedReveal(steps?.length ?? 0, isActive);

  if (!steps || steps.length === 0) return null;

  const visibleSteps = steps.slice(0, visibleCount);
  const allCompleted = steps.every((s) => s.status === "completed" || s.status === "failed");
  const headerText = !collapsed && !allCompleted ? "Thinking" : buildSummaryText(steps);

  return (
    <Box sx={{ mb: "1em", ...animationKeyframes }}>
      {/* Clickable Header */}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapse();
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          cursor: "pointer",
          py: "0.4em",
          px: "0.75em",
          borderRadius: "0.5em",
          backgroundColor: grey[50],
          border: `1px solid ${grey[100]}`,
          "&:hover": { backgroundColor: grey[100] },
          userSelect: "none",
        }}
      >
        {/* Always use the sparkle icon in the header. The per-step spinners in the expanded
            list already convey progress, so a second spinner here would be redundant. */}
        <AutoAwesomeIcon />
        <AnimatedStatusLabel text={headerText} showEllipsis={!allCompleted} />
        {!collapsed ? (
          <ExpandLess sx={{ fontSize: 16, color: grey[400] }} />
        ) : (
          <ExpandMore sx={{ fontSize: 16, color: grey[400] }} />
        )}
      </Box>

      {/* Expanded List */}
      <Collapse in={!collapsed}>
        <Box sx={{ mt: "0.5em", ml: "0.75em", pl: "0.75em", borderLeft: `2px solid ${grey[200]}` }}>
          {visibleSteps.map((step, i) => (
            <Box
              key={`${step.step}-${i}`}
              sx={{
                display: "flex",
                gap: "0.6em",
                alignItems: "flex-start",
                py: "0.25em",
                animation: "fadeSlideIn 0.3s ease-out",
              }}
            >
              {/* Match the text's first line box (0.75rem * 1.4 line-height) and center the icon
                  within it so the spinner/check lines up with the first line of the step text. */}
              <Box sx={{ display: "flex", alignItems: "center", height: "1.05rem", flexShrink: 0 }}>
                <StepIcon status={step.status} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: step.status === "failed" ? red[600] : step.status === "active" ? grey[800] : grey[500],
                    fontWeight: step.status === "active" ? 500 : 400,
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                    ...(step.status === "active" && {
                      "&::after": { content: '""', animation: "ellipsis 1.5s steps(4, end) infinite" },
                    }),
                  }}
                >
                  {getStepDisplayText(step)}
                </Typography>
                <StepDetail step={step} />
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
